import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Player } from "@/lib/types";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";
import EmailContentViewModal from "./EmailContentViewModal";

interface FlaggedPlayer {
  id: number;
  name: string;
  email: string;
  age?: number;
  hs_grad_year?: number;
  university_grad_year?: number;
  current_club?: string;
  specific_positions?: string[];
  hs_grad_year_in_email?: boolean;
  email_content?: string;
  position?: string[];
  nationality?: string;
  height?: string;
  weight?: string;
  playing_level?: string;
  gpa?: any;
  highlight_videos?: string[];
  distinguishments?: string[];
  international_caps?: any[];
}

interface GraduationYearWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  flaggedPlayers: FlaggedPlayer[];
  onProceedWithSelected: (selectedEmails: string[]) => void;
  onCancel: () => void;
  onPlayerUpdate?: (updatedPlayerData: Partial<FlaggedPlayer>) => void;
}

export default function GraduationYearWarningModal({
  isOpen,
  onClose,
  flaggedPlayers,
  onProceedWithSelected,
  onCancel,
  onPlayerUpdate,
}: GraduationYearWarningModalProps) {
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(
    new Set(flaggedPlayers.map(p => p.email))
  );
  const [viewingPlayer, setViewingPlayer] = useState<FlaggedPlayer | null>(null);
  const [showEmailContent, setShowEmailContent] = useState(false);

  const handlePlayerSelection = (email: string, checked: boolean) => {
    const newSelected = new Set(selectedEmails);
    if (checked) {
      newSelected.add(email);
    } else {
      newSelected.delete(email);
    }
    setSelectedEmails(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedEmails(new Set(flaggedPlayers.map(p => p.email)));
    } else {
      setSelectedEmails(new Set());
    }
  };

  const handlePlayerClick = (flaggedPlayer: FlaggedPlayer) => {
    setViewingPlayer(flaggedPlayer);
    setShowEmailContent(true);
  };

  const handlePlayerUpdateInModal = (updatedPlayerData: Partial<FlaggedPlayer>) => {
    // Update the viewing player if it's the same one
    if (viewingPlayer && viewingPlayer.id === updatedPlayerData.id) {
      setViewingPlayer(prev => prev ? { ...prev, ...updatedPlayerData } : null);
    }
    
    // Pass the update to parent component
    if (onPlayerUpdate) {
      onPlayerUpdate(updatedPlayerData);
    }
  };

  const handleProceed = () => {
    onProceedWithSelected(Array.from(selectedEmails));
  };

  const getWarningReason = (player: FlaggedPlayer) => {
    if (player.hs_grad_year_in_email === false) {
      return "Graduation year was inferred by our model (not explicitly mentioned)";
    }
    if (player.hs_grad_year === null || player.hs_grad_year === undefined) {
      if (player.university_grad_year === null || player.university_grad_year === undefined) {
        return "No graduation year information available";
      }
    }
    return "Graduation year accuracy concerns";
  };

  const renderEmailContentWithHighlight = (content: string | undefined, hsGradYear: number | undefined) => {
    if (!content || !hsGradYear) return content || "No email content available";
    
    // Highlight the graduation year in the email content
    const yearStr = hsGradYear.toString();
    const highlighted = content.replace(
      new RegExp(`\\b${yearStr}\\b`, 'gi'),
      `<mark style="background-color: #fef3c7; padding: 2px 4px; border-radius: 2px; font-weight: bold;">${yearStr}</mark>`
    );
    
    return highlighted;
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" style={{ zIndex: 60 }}>
          <DialogHeader>
            <DialogTitle className="flex items-center text-lg">
              <i className="fas fa-exclamation-triangle text-amber-500 mr-2"></i>
              Graduation Year Warning
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Warning Message */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <i className="fas fa-exclamation-triangle text-amber-400"></i>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800">
                    Graduation Year Accuracy Warning
                  </h3>
                  <div className="mt-2 text-sm text-amber-700">
                    <p>
                      Our system has flagged these players as not explicitly mentioning their graduation year 
                      in their email. This means our model has implied their graduation year, and is prone to 
                      a higher margin of error. Please double check that this information is correct before proceeding.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Player Selection Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedEmails.size === flaggedPlayers.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-neutral-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm font-medium">Select All ({flaggedPlayers.length})</span>
                </label>
                <span className="text-sm text-neutral-500">
                  {selectedEmails.size} of {flaggedPlayers.length} players selected
                </span>
              </div>
            </div>

            {/* Flagged Players List */}
            <div className="border border-neutral-200 rounded-lg overflow-hidden">
              <div className="bg-neutral-50 px-4 py-2 border-b border-neutral-200">
                <h4 className="text-sm font-medium text-neutral-700">
                  Players Requiring Review ({flaggedPlayers.length})
                </h4>
              </div>
              
              <div className="divide-y divide-neutral-200 max-h-96 overflow-y-auto">
                {flaggedPlayers.map((player) => (
                  <div key={player.id} className="p-4 hover:bg-neutral-50">
                    <div className="flex items-start space-x-3">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedEmails.has(player.email)}
                        onChange={(e) => handlePlayerSelection(player.email, e.target.checked)}
                        className="mt-1 rounded border-neutral-300 text-primary focus:ring-primary"
                      />
                      
                      {/* Avatar */}
                      <AvatarPlaceholder 
                        name={player.name} 
                        className="w-10 h-10 flex-shrink-0"
                      />
                      
                      {/* Player Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h5 className="text-sm font-medium text-neutral-900 truncate">
                                {player.name}
                              </h5>
                              <button
                                onClick={() => handlePlayerClick(player)}
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                                title="View player details"
                              >
                                <i className="fas fa-eye mr-1"></i>
                                View Details
                              </button>
                            </div>
                            
                            {/* Highlighted Player Info */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                              <div>
                                <span className="font-medium text-amber-600">Age:</span> 
                                <span className="ml-1 bg-amber-100 px-1 rounded">
                                  {player.age || 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-amber-600">HS Grad Year:</span> 
                                <span className="ml-1 bg-amber-100 px-1 rounded">
                                  {player.hs_grad_year || 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-amber-600">School:</span> 
                                <span className="ml-1 bg-amber-100 px-1 rounded">
                                  {player.current_club || 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-amber-600">Position:</span> 
                                <span className="ml-1 bg-amber-100 px-1 rounded">
                                  {player.specific_positions?.join(', ') || 
                                   player.position?.join(', ') || 'N/A'}
                                </span>
                              </div>
                            </div>
                            
                            {/* Warning Reason */}
                            <div className="mt-2 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                              <i className="fas fa-info-circle mr-1"></i>
                              {getWarningReason(player)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3">
              <Button variant="outline" onClick={onCancel}>
                <i className="fas fa-times mr-2"></i>
                Cancel Email
              </Button>
              <Button 
                onClick={handleProceed}
                disabled={selectedEmails.size === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <i className="fas fa-paper-plane mr-2"></i>
                Proceed with emails to selected players ({selectedEmails.size})
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Content View Modal */}
      {viewingPlayer && (
        <EmailContentViewModal
          player={viewingPlayer}
          isOpen={showEmailContent}
          onClose={() => {
            setShowEmailContent(false);
            setViewingPlayer(null);
          }}
          onPlayerUpdate={handlePlayerUpdateInModal}
        />
      )}
    </>
  );
} 